import { create } from 'zustand';

/**
 * Zustand store for salon dashboard state.
 *
 * Initial state is INTENTIONALLY empty. Earlier this file initialized
 * clients/appointments/services/team/reviews/settings with hardcoded
 * demo data (Max/Bella/Luna, "Happy Paws" salon, etc.) which flashed
 * on-screen for the split-second before the real backend data landed —
 * the "ghost data" bug. Consumers must now gate their UI on their own
 * loading state until they've called the corresponding setter.
 *
 * `reset()` is called by AuthContext.logout so a second user logging in
 * on the same browser tab never sees the previous salon's data.
 */

const fmt = (d) => d.toISOString().split('T')[0];
const timeStr = (d) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// Empty-but-shape-valid defaults. `settings` is nested — keeping the
// structure prevents `settings.general.name` crashes in downstream
// components even before the real settings arrive from the backend.
const EMPTY_SETTINGS = {
  general: {
    name: '', description: '',
    street: '', streetNumber: '', block: '', staircase: '', apartment: '',
    zone: '', city: '', county: '', postalCode: '',
    latitude: null, longitude: null,
    phone: '', email: '', website: '',
  },
  schedule: {
    monday:    { open: true,  start: '09:00', end: '18:00' },
    tuesday:   { open: true,  start: '09:00', end: '18:00' },
    wednesday: { open: true,  start: '09:00', end: '18:00' },
    thursday:  { open: true,  start: '09:00', end: '18:00' },
    friday:    { open: true,  start: '09:00', end: '18:00' },
    saturday:  { open: true,  start: '10:00', end: '16:00' },
    sunday:    { open: false, start: '09:00', end: '18:00' },
  },
  closedDates: [],
  photos: { coverPhoto: null, logo: null, gallery: [] },
  notifications: {
    notifyCustomerConfirmation: true,
    notifyCustomerReminder24h: true,
    notifyCustomerReminder1h: true,
    notifyOwnerNewBooking: true,
    marketingFollowup45Days: true,
    autoConfirmBookings: true,
  },
};

const initialState = {
  clients: [],
  appointments: [],
  services: [],
  team: [],
  reviews: [],
  settings: EMPTY_SETTINGS,
};

const useSalonStore = create((set, get) => ({
  /* ── State ── */
  ...initialState,

  /* ── Reset (called from AuthContext.logout to wipe the previous
        owner's data before the next user lands on the same tab) ── */
  reset: () => set(() => ({ ...initialState })),

  /* ── Client actions ── */
  addClient: (client) =>
    set((state) => ({
      clients: [{ createdAt: new Date(), ...client }, ...state.clients],
    })),

  deleteClient: (id) =>
    set((state) => {
      const client = state.clients.find((c) => c.id === id);
      if (!client) return state;
      return {
        clients: state.clients.filter((c) => c.id !== id),
        appointments: state.appointments.filter((a) => a.client !== client.name),
      };
    }),

  /* ── Appointment actions ── */
  addAppointment: (appt) =>
    set((state) => ({
      appointments: [...state.appointments, appt],
    })),

  // Bulk-replace (used after the dashboard fetches bookings from the backend).
  setAppointments: (list) =>
    set(() => ({
      appointments: Array.isArray(list) ? list : [],
    })),

  deleteAppointment: (id) =>
    set((state) => ({
      appointments: state.appointments.filter((a) => a.id !== id),
    })),

  updateAppointment: (id, updates) =>
    set((state) => ({
      appointments: state.appointments.map((a) =>
        a.id === id ? { ...a, ...updates } : a,
      ),
    })),

  updateAppointmentStatus: (id, status) =>
    set((state) => ({
      appointments: state.appointments.map((a) =>
        a.id === id ? { ...a, status } : a,
      ),
    })),

  moveAppointment: (id, newStart, newEnd) =>
    set((state) => ({
      appointments: state.appointments.map((a) =>
        a.id === id
          ? { ...a, start: newStart, end: newEnd, date: fmt(newStart), time: timeStr(newStart) }
          : a,
      ),
    })),

  resizeAppointment: (id, newStart, newEnd) =>
    set((state) => ({
      appointments: state.appointments.map((a) =>
        a.id === id
          ? { ...a, start: newStart, end: newEnd, date: fmt(newStart), time: timeStr(newStart) }
          : a,
      ),
    })),

  /* ── Settings actions ── */
  updateSettings: (newSettings) =>
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    })),

  /* ── Derived: get appointments for a specific client ── */
  getClientHistory: (clientName) => {
    return get().appointments
      .filter((a) => a.client === clientName)
      .sort((a, b) => b.date.localeCompare(a.date));
  },
}));

export default useSalonStore;
